// ─── Imports ────────────────────────────────────────────────────────────
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
  } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Container,
  Row,
  Col,
  ListGroup,
  Accordion,
  Badge,
  Modal,
  Button,
  ButtonGroup,
} from 'react-bootstrap';
 import {
  GeoAltFill,
  GearFill,
  Search,
} from 'react-bootstrap-icons';
import { MessageList } from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';
import { mapRow } from './mapRow';
import { useMediaQuery } from 'react-responsive';
import axios from '../axiosAlertConfig';
import useOperationalUpdates from '../../hooks/useOperationalUpdates';
import IncidentAlertChat from './IncidentAlertChat';
import DashboardCardHeader from '../ViewReports/DashboardCardHeader';
import logo from '../ViewReports/SchoolShield_color300x3xx.webp';
import { UserContext } from '../../userAlertContext';
import { Loader } from '@googlemaps/js-api-loader';

import DirectMessageModal from './DirectMessageModal';
import IncidentSpecialStudentsModal from './IncidentSpecialStudentsModal';
import LocationEditorModal from './LocationEditorModal';
import NotCheckedInModal from './NotCheckedInModal';
import IncidentMessageSearchModal from './IncidentMessageSearchModal';

// ─── Helpers ────────────────────────────────────────────────────────────
const parseStatus = raw => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return [String(raw)]; }
};

const parseJsonArray = raw => {
  if (Array.isArray(raw)) return raw;
  if (raw == null) return [];

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return trimmed
        .split(',')
        .map(value => value.trim())
        .filter(Boolean);
    }
  }

  return [raw];
};

const normalizeIncidentUser = (
  rawUser,
  activeBuildingID,
  activeBuildingName
) => {
  const user = rawUser || {};
  const activeID = String(activeBuildingID || '').trim();
  const activeName = String(activeBuildingName || '').trim();
  const campusIDs = parseJsonArray(user.campusIDs);
  const campusNames = parseJsonArray(user.campusNames);
  const assignmentsByID = new Map();

  campusIDs.forEach((rawID, index) => {
    const id = String(rawID ?? '').trim();
    if (!id) return;

    const suppliedName = String(campusNames[index] ?? '').trim();
    const name = suppliedName || (id === activeID ? activeName : '') || id;
    const existing = assignmentsByID.get(id);

    if (!existing || (existing.name === existing.id && suppliedName)) {
      assignmentsByID.set(id, { id, name });
    }
  });

  // The server query only returns users assigned to the active building.
  // Add the active building as a defensive fallback if a legacy row has
  // malformed or incomplete campusIDs JSON.
  if (activeID && !assignmentsByID.has(activeID)) {
    assignmentsByID.set(activeID, {
      id: activeID,
      name: activeName || activeID,
    });
  }

  const assignedBuildings = Array.from(assignmentsByID.values()).sort((a, b) => {
    if (a.id === activeID && b.id !== activeID) return -1;
    if (b.id === activeID && a.id !== activeID) return 1;
    return String(a.name || a.id).localeCompare(String(b.name || b.id));
  });

  return {
    ...user,
    iUserID: String(user.iUserID ?? '').trim(),
    assignedBuildings,
    assignmentGroup: assignedBuildings.length > 1 ? 'multiple' : 'exclusive',
  };
};

const createEmptyCheckInGroups = () => ({
  exclusive: { total: 0, checkedIn: 0, people: [], notCheckedIn: [] },
  multiple: { total: 0, checkedIn: 0, people: [], notCheckedIn: [] },
});

const buildCheckInGroups = rosterRows => {
  const groups = createEmptyCheckInGroups();

  (rosterRows || []).forEach(row => {
    if (!row?.fromUserTable) return;

    const groupKey = row.assignmentGroup === 'multiple'
      ? 'multiple'
      : 'exclusive';
    const group = groups[groupKey];

    group.total += 1;
    group.people.push(row);

    if (row.hasCheckedIn) {
      group.checkedIn += 1;
    } else {
      group.notCheckedIn.push(row);
    }
  });

  Object.values(groups).forEach(group => {
    group.people.sort((a, b) => {
      if (Boolean(a?.hasCheckedIn) !== Boolean(b?.hasCheckedIn)) {
        return a?.hasCheckedIn ? 1 : -1;
      }

      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });

    group.notCheckedIn.sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''))
    );
  });

  return groups;
};

const checkInPercent = group => {
  const total = Math.max(0, Number(group?.total) || 0);
  const checkedIn = Math.min(
    total,
    Math.max(0, Number(group?.checkedIn) || 0)
  );

  return total ? Math.round((checkedIn / total) * 100) : 0;
};

function CompactCheckInStatusBar({
  label,
  group,
  position = 'single',
  onClick,
}) {
  const total = Math.max(0, Number(group?.total) || 0);
  const checkedIn = Math.min(
    total,
    Math.max(0, Number(group?.checkedIn) || 0)
  );
  const outstanding = Math.max(0, total - checkedIn);
  const percent = checkInPercent(group);
  const radius =
    position === 'top'
      ? '4px 4px 0 0'
      : position === 'bottom'
      ? '0 0 4px 4px'
      : '4px';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open Check In Status for ${label}. ${outstanding} of ${total} people have not checked in. ${percent}% checked in.`}
      title={`Open Check In Status — ${label}`}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height: 18,
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        border: 0,
        borderRadius: radius,
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundColor: total > 0 ? '#dc3545' : '#6c757d',
        borderTop:
          position === 'bottom'
            ? '1px solid rgba(255,255,255,0.65)'
            : 'none',
        color: '#ffffff',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '0 auto 0 0',
          width: `${percent}%`,
          backgroundColor: '#198754',
          transition: 'width 180ms ease',
        }}
      />

      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '0 7px',
          color: '#ffffff',
          fontSize: '0.7rem',
          fontWeight: 800,
          lineHeight: 1,
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={label}
        >
          {label}
        </span>
        <span style={{ flexShrink: 0 }}>
          {outstanding} of {total} out · {percent}%
        </span>
      </span>
    </button>
  );
}

const srpColour = txt => {
  const k = txt.toLowerCase();
  if (k.includes('lock'))    return 'danger';
  if (k.includes('evac'))    return 'warning';
  if (k.includes('secure'))  return 'primary';
  if (k.includes('shelter')) return 'info';
  if (k.includes('hold'))    return 'secondary';
  return 'dark';
};

// NEW: helper used only for header badges
const headerLocation = (inc) =>
  (inc &&
    (inc.buildingName ||
      inc.campusName ||
      inc.myLocation ||
      inc.incidentLocation ||
      '')) ||
  '';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// single Loader instance
const googleMapsLoader = new Loader({
  apiKey   : GOOGLE_MAPS_API_KEY,
  version  : 'weekly',
  libraries: ['marker'],
});

// ─────────────────────── A. Active‑Incidents list ───────────────────────
// ─────────────────────── A. Active‑Incidents list ───────────────────────
function ActiveIncidentsList({
  onSelect,
  selectedId,
  iCampusID,
  iBuildingID,
  activeOnly,
  onLoaded,
  last24hOnly = false,
}) {
  const { user } = useContext(UserContext);
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    if (!user?.iAccountID) return;
    axios
      .get('/ialert/incidents', {
        params: {
          iAccountID : user.iAccountID,
          iCampusID,
          iBuildingID,
          activeOnly : activeOnly ? '1' : '0',
        },
      })
      .then(({ data }) => {
        const filtered = last24hOnly
          ? data.filter(
              inc =>
                Date.now() - new Date(inc.createdAt).getTime() <=
                24 * 60 * 60 * 1000
            )
          : data;
        setIncidents(filtered);
        onLoaded?.(filtered);
      })
      .catch(console.error);
  }, [
    user?.iAccountID,
    iCampusID,
    iBuildingID,
    activeOnly,
    onLoaded,
    last24hOnly,
  ]);

  return (
    <ListGroup variant="flush">
      {incidents.map(inc => {
        // build a nice location string from whatever fields are present
        const locationParts = [
          inc.buildingName,             // primary building/campus name
          inc.myLocation,               // optional detailed room/location
          inc.incidentLocation,         // fallback if your incident has this field
        ].filter(Boolean);

        const locationText = locationParts.join(' — ');

        return (
          <ListGroup.Item
            key={inc.incidentID}
            action
            active={selectedId === inc.incidentID}
            onClick={() => onSelect(inc)}
            className="d-flex align-items-center"
            style={{
              background:
                selectedId === inc.incidentID
                  ? '#0d6efd'
                  : inc.isActive
                  ? inc.color ?? '#ffeb99'
                  : '#d3d3d3',
              color: selectedId === inc.incidentID ? '#ffffff' : '#000000',
            }}
          >
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <div>
                  <Badge bg={srpColour(inc.incidentType)} className="me-1">
                    <b>{inc.incidentType}</b>
                  </Badge>
                  {' '}– {inc.incidentCategory}
                </div>

                {locationText && (
                  <div className="text-muted small mt-1">
                    {locationText}
                  </div>
                )}
              </div>

              <small className="text-muted ms-2">
                {new Date(inc.createdAt).toLocaleTimeString()}
              </small>
            </div>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
}


// ─── STATUS_OPTIONS ─────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  ['Need medical attention', 'danger'],
  ['Someone is missing', 'warning'],
  ['Everyone is here', 'success'],
];

/* ── Robust helper: always return one of the 3 canonical labels ───────── */
 const pickLabel = raw => getCanonicalLabelOrNull(raw) || STATUS_OPTIONS[2][0];

const labelToVariant = label =>
  STATUS_OPTIONS.find(([l]) => l === label)?.[1] || 'success';

/* ── NEW: map canonical label → severity rank (lower = worse) ────────── */
const STATUS_RANK = STATUS_OPTIONS.reduce((acc, [label], idx) => {
  acc[label] = idx;
  return acc;
}, {});

/* Return ALL canonical labels from myStatus, preserving saved order */
const getCanonicalLabels = (raw) => {
  const arr = parseStatus(raw);
  const tokens = (Array.isArray(arr) ? arr : [arr])
    .flatMap(v => String(v ?? '').split(/\s*,\s*/))
    .map(v => v.trim())
    .filter(Boolean);

  const found = [];

  tokens.forEach(token => {
    const match = STATUS_OPTIONS.find(
      ([lbl]) => lbl.toLowerCase() === token.toLowerCase()
    )?.[0];

    if (match && !found.includes(match)) found.push(match);
  });

  return found;
};

/* Return the WORST canonical label for severity / sorting / colors */
const getCanonicalLabelOrNull = (raw) => {
  const labels = getCanonicalLabels(raw);
  if (!labels.length) return null;

  return labels.reduce((worst, label) => {
    if (!worst) return label;
    return (STATUS_RANK[label] ?? 99) < (STATUS_RANK[worst] ?? 99)
      ? label
      : worst;
  }, null);
};

/* ── NEW: walk a sender’s thread newest→oldest, return latest non-empty ─ */
const latestStatusLabelFromThread = (thread) => {
  for (let i = thread.length - 1; i >= 0; i--) {
    const label = getCanonicalLabelOrNull(thread[i].myStatus);
    if (label) return label;
  }
  return STATUS_OPTIONS[2][0];
};

 // Build people array from raw messages (ASC by createdAt)
const computePeopleFromMessages = (msgs) => {
  const threads = (msgs || []).reduce((acc, m) => {
    const key = m.senderUserID || m.senderName || m.messageID;
    if (!key) return acc;
    (acc[key] ||= []).push(m);
    return acc;
  }, {});

  const persons = Object.entries(threads).map(([key, thread]) => {
    // ensure stable ordering for the message modal
    const sortedThread = [...thread].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    const lastMsg       = sortedThread[sortedThread.length - 1];
    const statusLabel   = latestStatusLabelFromThread(sortedThread);
    const statusVariant = labelToVariant(statusLabel);

    return {
      key: String(key),
      userId: lastMsg?.senderUserID != null ? String(lastMsg.senderUserID) : null,
      name         : lastMsg?.senderName || 'Unknown',
      statusLabel,
      statusVariant,
      count        : sortedThread.length,
      messages     : sortedThread,
    };
  });

  persons.sort((a, b) => {
    const sa = STATUS_RANK[a.statusLabel] ?? 99;
    const sb = STATUS_RANK[b.statusLabel] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.name || '').localeCompare(b.name || '');
  });

  return persons;
};



// Build "latest status + latest location" per user from raw messages (ASC by createdAt)
 const computeRosterFromMessages = (msgs) => {
  const byUser = new Map();

  (msgs || []).forEach(m => {
    const userId = m.senderUserID || null;
    const key    = userId || m.senderName || m.messageID;
    if (!key) return;

    let rec = byUser.get(key);
    if (!rec) {
      rec = {
        key,
        userId,
        name               : m.senderName || 'Unknown',
        mobile             : m.senderMobileNumber || '',
        latestStatusLabel  : null,   // worst status only (for sorting/color)
        latestStatusLabels : [],     // all statuses from latest update
        latestStatusDisplay: '',     // text shown in the pill
        latestStatusVariant: 'secondary',
        latestStatusTime   : null,
        latestLocation     : '',
        latestLocationTime : null,
        hasCheckedIn       : true,
      };
      byUser.set(key, rec);
    }

    const msgTs = m.createdAt ? new Date(m.createdAt).getTime() : 0;

    const prevLocTs = rec.latestLocationTime
      ? new Date(rec.latestLocationTime).getTime()
      : -Infinity;

    if (m.myLocation && String(m.myLocation).trim() && msgTs >= prevLocTs) {
      rec.latestLocation     = String(m.myLocation).trim();
      rec.latestLocationTime = m.createdAt;
    }

    const labels = getCanonicalLabels(m.myStatus);
    const worstLabel = getCanonicalLabelOrNull(m.myStatus);

    const prevStatusTs = rec.latestStatusTime
      ? new Date(rec.latestStatusTime).getTime()
      : -Infinity;

    if (labels.length && worstLabel && msgTs >= prevStatusTs) {
      rec.latestStatusLabels  = labels;
      rec.latestStatusLabel   = worstLabel;
      rec.latestStatusDisplay = labels.join(', ');
      rec.latestStatusVariant = labelToVariant(worstLabel);
      rec.latestStatusTime    = m.createdAt;
    }
  });

  const rows = Array.from(byUser.values());

  rows.forEach(r => {
    if (!r.latestStatusLabel) {
      r.latestStatusLabels  = [STATUS_OPTIONS[2][0]];
      r.latestStatusLabel   = STATUS_OPTIONS[2][0];
      r.latestStatusDisplay = STATUS_OPTIONS[2][0];
      r.latestStatusVariant = labelToVariant(r.latestStatusLabel);
    }
  });

  rows.sort((a, b) => {
    const sa = STATUS_RANK[a.latestStatusLabel] ?? 99;
    const sb = STATUS_RANK[b.latestStatusLabel] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.name || '').localeCompare(b.name || '');
  });

  return rows;
};

// Merge roster from messages with Users for this incident scope.
// Users without any incident messages are marked as not checked in.
const mergeRosterWithUsers = (
  messageRoster,
  users,
  activeBuildingID,
  activeBuildingName
) => {
  const merged = [...messageRoster];
  const byUserId = new Map();

  messageRoster.forEach(row => {
    if (row.userId) byUserId.set(String(row.userId), row);
  });

  const normalizedUsers = (users || []).map(user =>
    normalizeIncidentUser(user, activeBuildingID, activeBuildingName)
  );

  normalizedUsers.forEach(userRow => {
    const uid = String(userRow.iUserID);
    const existing = byUserId.get(uid);
    const displayName =
      [userRow.firstName, userRow.lastName].filter(Boolean).join(' ') ||
      userRow.username ||
      uid;

    if (existing) {
      if (!existing.name || existing.name === 'Unknown') {
        existing.name = displayName;
      }
      if (!existing.mobile && userRow.phoneNumber) {
        existing.mobile = userRow.phoneNumber;
      }

      existing.userId = uid;
      existing.username = userRow.username || '';
      existing.email = userRow.email || '';
      existing.assignedBuildings = userRow.assignedBuildings;
      existing.assignmentGroup = userRow.assignmentGroup;
      existing.fromUserTable = true;
      existing.hasCheckedIn = true;
      return;
    }

    merged.push({
      key: `user-${uid}`,
      userId: uid,
      name: displayName,
      username: userRow.username || '',
      mobile: userRow.phoneNumber || '',
      email: userRow.email || '',
      assignedBuildings: userRow.assignedBuildings,
      assignmentGroup: userRow.assignmentGroup,
      fromUserTable: true,
      latestStatusLabel: 'Not checked in',
      latestStatusLabels: [],
      latestStatusDisplay: 'Not checked in',
      latestStatusVariant: 'secondary',
      latestStatusTime: null,
      latestLocation: '',
      latestLocationTime: null,
      hasCheckedIn: false,
    });
  });

  return merged;
};



 /* --------------------------------------------------------------------- */
function PeopleStatus({
  incidentID,
  iAccountID,
  iBuildingID,
  buildingName = '',
  myLocation  = '',
}) {
  const [showModal, setShowModal] = useState(false);
  const [showNotCheckedInModal, setShowNotCheckedInModal] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [dmPeer, setDmPeer] = useState(null);

  // Checked-in roster rows shown directly in the column.
  const [roster, setRoster] = useState([]);
  const [rosterSearch, setRosterSearch] = useState('');
  const [checkInGroups, setCheckInGroups] = useState(createEmptyCheckInGroups);

  // local helper: make phone dialable
  const dialTel = (v) => {
    const raw = String(v ?? '').trim();
    if (!raw) return '';
    const plus = raw.startsWith('+') ? '+' : '';
    const digits = raw.replace(/[^\d]/g, '');
    return digits ? `${plus}${digits}` : '';
  };

  const refresh = useCallback((signal) => {
    if (!incidentID || !iAccountID || !iBuildingID) {
      setRoster([]);
      setCheckInGroups(createEmptyCheckInGroups());
      return;
    }

    return Promise.all([
      axios.get(`/ialert/incidentmessages/${incidentID}`, { signal }),
      axios.get(`/ialert/incidentmessages/${incidentID}/users`, {
        params: { iAccountID, iBuildingID }, signal,
      }),
    ])
      .then(([msgRes, userRes]) => {
        if (signal?.aborted) return;
        const msgs  = msgRes.data?.messages || [];
        const users = userRes.data?.users || [];

        // message threads (for msg count + modal)
        const people = computePeopleFromMessages(msgs);

        const peopleByUserId = new Map();
        const peopleByKey    = new Map();
        const peopleByName   = new Map();

        people.forEach(p => {
          peopleByKey.set(String(p.key), p);
          if (p.userId) peopleByUserId.set(String(p.userId), p);
          if (p.name) peopleByName.set(String(p.name), p);
        });

        // Latest status + location, plus the assigned-building roster.
        const msgRoster = computeRosterFromMessages(msgs);
        const fullRoster = mergeRosterWithUsers(
          msgRoster,
          users,
          iBuildingID,
          buildingName
        );

        // combine roster row + message data
        const combined = fullRoster.map(r => {
          const p =
            (r.userId && peopleByUserId.get(String(r.userId))) ||
            peopleByKey.get(String(r.key)) ||
            peopleByName.get(String(r.name)) ||
            null;

          return {
            ...r,
            messageCount: p?.count || 0,
            messages: p?.messages || [],
            // sender name used in messages (may differ from Users-table display name)
            threadSenderName: p?.name || r.name || 'Unknown',
          };
        });

        // The main column only shows checked-in people. Medical attention
        // is always first, missing people second, and all other check-ins last.
        const checkedInRows = combined.filter(row => row.hasCheckedIn);
        const rankOf = row => {
          if (row.latestStatusVariant === 'danger') return 0;
          if (row.latestStatusVariant === 'warning') return 1;
          if (row.latestStatusVariant === 'success') return 2;
          return 99;
        };

        checkedInRows.sort((a, b) => {
          const ra = rankOf(a);
          const rb = rankOf(b);
          if (ra !== rb) return ra - rb;
          return String(a.name || '').localeCompare(String(b.name || ''));
        });

        setRoster(checkedInRows);
        setCheckInGroups(buildCheckInGroups(combined));
      })
      .catch(error => {
        if (signal?.aborted || error?.code === 'ERR_CANCELED') return;
        console.error(error);
        setRoster([]);
        setCheckInGroups(createEmptyCheckInGroups());
      });
  }, [incidentID, iAccountID, iBuildingID, buildingName]);

  useEffect(() => {
    setShowNotCheckedInModal(false);
    setRosterSearch('');
    setRoster([]);
    setCheckInGroups(createEmptyCheckInGroups());
  }, [incidentID]);

  useOperationalUpdates({
    client: axios,
    path: `/ialert/incidentmessages/${encodeURIComponent(incidentID || '')}/live-events`,
    iAccountID, iBuildingID,
    app: 'IncidentAlert',
    enabled: Boolean(incidentID && iAccountID && iBuildingID),
    fallbackMs: 5000,
    refreshKey: `${incidentID || ''}:${iAccountID || ''}:${iBuildingID || ''}:${buildingName}`,
    onRefresh: refresh,
  });

  const filteredRoster = useMemo(() => {
    const term = rosterSearch.trim().toLowerCase();
    if (!term) return roster;
    return roster.filter(r =>
      [
        r.name,
        r.mobile,
        r.latestStatusDisplay || r.latestStatusLabel,
        r.latestLocation,
        String(r.hasCheckedIn ? 'Yes' : 'No'),
      ]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(term))
    );
  }, [roster, rosterSearch]);

  const activeBuildingLabel =
    String(buildingName || '').trim() ||
    String(iBuildingID || '').trim() ||
    'Active incident building';

  const handleExportRoster = () => {
    if (!filteredRoster.length) return;

    const header = [
      'Name',
      'Mobile',
      'Status',
      'StatusUpdated',
      'Location',
      'LocationUpdated',
      'CheckedIn',
    ];
    const lines = [header.join(',')];

    filteredRoster.forEach(r => {
      const cells = [
        r.name || '',
        r.mobile || '',
        r.latestStatusDisplay || r.latestStatusLabel || '',
        r.latestStatusTime ? new Date(r.latestStatusTime).toISOString() : '',
        r.latestLocation || '',
        r.latestLocationTime ? new Date(r.latestLocationTime).toISOString() : '',
        r.hasCheckedIn ? 'Yes' : 'No',
      ];
      lines.push(cells.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    });

    const csv  = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `incident_${incidentID}_latest_status_location.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTs = ts => (ts ? new Date(ts).toLocaleString() : '—');
  const tinyTsStyle = { fontSize: '0.65rem', lineHeight: 1.05 };

  const rowClassFor = (r) => {
    if (r.latestStatusVariant === 'danger')  return 'table-danger';
    if (r.latestStatusVariant === 'warning') return 'table-warning';
    if (r.latestStatusVariant === 'success') return 'table-success';
    return '';
  };

  const badgeVariantFor = (r) => r.latestStatusVariant || 'secondary';

  return (
    <>
      <div className="mb-1">
        <CompactCheckInStatusBar
          label={activeBuildingLabel}
          group={checkInGroups.exclusive}
          position="top"
          onClick={() => setShowNotCheckedInModal(true)}
        />
        <CompactCheckInStatusBar
          label="Other Campuses"
          group={checkInGroups.multiple}
          position="bottom"
          onClick={() => setShowNotCheckedInModal(true)}
        />
      </div>

      <div className="mb-1" style={{ fontSize: '10px' }}>
        Medical attention and missing people stay at the top. Select either check-in status bar for the full roster.
      </div>

      {/* search + export for checked-in people */}
      <div className="d-flex align-items-center mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search checked-in people by name, status, or location…"
          value={rosterSearch}
          onChange={e => setRosterSearch(e.target.value)}
        />
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2 flex-shrink-0"
          onClick={handleExportRoster}
          disabled={!filteredRoster.length}
        >
          Export CSV
        </Button>
      </div>

      {/* compact table: combine Status + Status updated + Location + Msgs into ONE column */}
      <div className="table-responsive">
        <table className="table table-bordered table-sm align-middle mb-0">
          <thead>
            <tr>
              <th style={{fontSize:'12px'}}>Name</th>
              {/* <th>Mobile</th> */}
              <th style={{fontSize:'12px'}}>Status / Location / Msgs</th>
              {/* <th>Checked in</th> */}
            </tr>
          </thead>

<tbody>
  {filteredRoster.map(r => {
    const tel = dialTel(r.mobile);
    const mobileTxt = String(r.mobile || '').trim();
    const hasMobile = !!mobileTxt;

    return (
      <tr key={r.key} className={rowClassFor(r)}>
        {/* Name + phone (phone BELOW name) */}
        <td style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
            {r.name}
          </div>

          <div style={{ marginTop: 4, fontSize: '0.85rem', lineHeight: 1.1 }}>
            {hasMobile ? (
              tel ? (
                <a
                  href={`tel:${tel}`}
                  title="Tap to call"
                  style={{ textDecoration: 'none', fontWeight: 700 }}
                >
                  <span aria-hidden="true" style={{ marginRight: 6, fontSize: '0.9em' }}>
                    📞
                  </span>
                  {mobileTxt}
                </a>
              ) : (
                <span className="text-muted">
                  <span aria-hidden="true" style={{ marginRight: 6, fontSize: '0.9em' }}>
                    📞
                  </span>
                  {mobileTxt}
                </span>
              )
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>

        {/* Status / Updated / Location / Msgs + Checked-in icon */}
        <td>
          <div className="d-flex justify-content-between align-items-start gap-2">
            {/* Left content */}
            <div style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
   

   <Badge
  pill
  bg={badgeVariantFor(r)}
  text={badgeVariantFor(r) === 'warning' ? 'dark' : undefined}
  style={{
    maxWidth: '100%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    fontWeight: 800,
    lineHeight: 1.15,
  }}
>
  {r.latestStatusDisplay || r.latestStatusLabel}
</Badge>

                <small className="text-muted" style={tinyTsStyle}>
                  {formatTs(r.latestStatusTime)}
                </small>
              </div>

              <div
                className="d-flex align-items-center gap-2 flex-wrap mt-1"
                style={{ minWidth: 0 }}
              >
                <Badge
                  pill
                  bg="secondary"
                  style={{
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    fontWeight: 800,
                  }}
                >


<GeoAltFill size={18} />

                  {r.latestLocation ? ` ${r.latestLocation}` : 'Loc: —'}
                </Badge>

                <span className="text-muted" style={tinyTsStyle}>
                  {formatTs(r.latestLocationTime)}
                </span>
              </div>
            </div>

            {/* Right controls: msgs + checked-in icon */}
            <div className="text-end flex-shrink-0 d-flex flex-column align-items-end gap-1">
              <Button
                size="sm"
                 variant="primary"
                disabled={!r.messageCount}
                onClick={() => {
                  setSelectedAuthor({
                    name: r.name || 'Unknown',
                    threadSenderName: r.threadSenderName || r.name || 'Unknown',
                    messages: r.messages || [],
                  });
                  setShowModal(true);
                }}
                title={r.messageCount ? 'View messages' : 'No messages'}
              >
                {r.messageCount || 0}
              </Button>

              <span
                title={r.hasCheckedIn ? 'Checked in' : 'Not checked in'}
                aria-label={r.hasCheckedIn ? 'Checked in' : 'Not checked in'}
                style={{
                  fontSize: '2.1rem',
                  lineHeight: 1,
                  fontWeight: 900,
                  color: r.hasCheckedIn ? '#0d6efd' : '#dc3545', // blue ✓, red X
                  userSelect: 'none',
                }}
              >
                {r.hasCheckedIn ? '✓' : '✕'}
              </span>
            </div>
          </div>
        </td>
      </tr>
    );
  })}

  {!filteredRoster.length && (
    <tr>
      <td colSpan={2} className="text-center text-muted">
        {roster.length
          ? 'No checked-in people match your search.'
          : 'No one has checked in yet.'}
      </td>
    </tr>
  )}
</tbody>



        </table>
      </div>

      <NotCheckedInModal
        show={showNotCheckedInModal}
        onHide={() => setShowNotCheckedInModal(false)}
        buildingName={buildingName}
        buildingID={iBuildingID}
        exclusiveGroup={checkInGroups.exclusive}
        multipleGroup={checkInGroups.multiple}
      />

      {/* message thread modal (opens from msg count) */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">
            Messages by {selectedAuthor?.name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight:'60vh', overflowY:'auto', padding:0 }}>
          <MessageList
            className="rce-message-list"
            dataSource={(selectedAuthor?.messages || [])
              .map(m => mapRow(
                m,
                selectedAuthor?.threadSenderName || selectedAuthor?.name,
                buildingName,
                myLocation,
                parseStatus(m.myStatus)
              ))
              .filter(Boolean)}
          />
        </Modal.Body>
      </Modal>

      {/* direct-message modal (unchanged placeholder) */}
      {dmPeer && (
        <DirectMessageModal
          show={!!dmPeer}
          onHide={() => setDmPeer(null)}
          peer={dmPeer}
        />
      )}
    </>
  );
}

// ─── Map colouring helpers ──────────────────────────────────────────────
const VARIANT_COLORS = {
  danger   : '#dc3545',
  warning  : '#ffc107',
  success  : '#28a745',
  primary  : '#0d6efd',
  info     : '#0dcaf0',
  secondary: '#6c757d',
  dark     : '#000000',
};

const extractVariant = (raw) => labelToVariant(pickLabel(raw));


// ─── Map InfoWindow helpers (ADD right above MapView) ────────────────────
const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const cleanTel = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  // keep leading + if present, strip everything else except digits
  const plus = raw.startsWith('+') ? '+' : '';
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? `${plus}${digits}` : '';
};

const statusPillHtml = (label, variant) => {
  const bg = VARIANT_COLORS[variant] || VARIANT_COLORS.secondary;
  const text = variant === 'warning' ? '#000' : '#fff';
  return `
    <span style="
      display:inline-block;
      padding:2px 10px;
      border-radius:999px;
      background:${bg};
      color:${text};
      font-weight:800;
      font-size:12px;
      line-height:18px;
      border:1px solid rgba(0,0,0,0.15);
    ">
      ${escapeHtml(label)}
    </span>
  `;
};










// ──────────────────────── C. Map / Floor‑plan ───────────────────────────
function MapView({ incident }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [coords, setCoords] = useState([]);

  // ✅ ADD: refresh button state + 5s cooldown
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0); // epoch ms
  const cooldownUntilRef = useRef(0);
  const cooldownTimerRef = useRef(null);

  // ✅ ADD: shared fetch function (used on load + button click)
  const loadCoords = useCallback((signal) => {
    if (!incident?.incidentID) {
      setCoords([]);
      return Promise.resolve();
    }

    setIsRefreshing(true);

    return axios
      .get(`/ialert/incidentmessages/${incident.incidentID}`, { signal })
      .then(({ data }) => {
        if (signal?.aborted) return;
        /* keep only the most‑recent non‑empty geo per user (createdAt DESC) */
        const latestByUser = {};
        (data.messages || []).forEach(m => {
          if (m.latitude == null || m.longitude == null) return;
          const key = m.senderUserID || m.senderName || m.messageID;
          if (
            !latestByUser[key] ||
            new Date(m.createdAt) > new Date(latestByUser[key].createdAt)
          ) {
            latestByUser[key] = m;
          }
        });

        const pts = Object.values(latestByUser).map(m => {
          const statusLabel = pickLabel(m.myStatus);          // -> one of your 3 labels
          const statusVariant = labelToVariant(statusLabel);  // danger/warning/success

          return {
            lat: m.latitude,
            lng: m.longitude,
            variant: statusVariant,                           // marker color
            sender: m.senderName,
            mobile: m.senderMobileNumber,
            statusLabel,                                      // clean label
            ts: m.createdAt,
          };
        });

        setCoords(pts);
      })
      .catch(error => { if (!signal?.aborted && error?.code !== 'ERR_CANCELED') console.error(error); })
      .finally(() => { if (!signal?.aborted) setIsRefreshing(false); });
  }, [incident?.incidentID]);

  useOperationalUpdates({
    client: axios,
    path: `/ialert/incidentmessages/${encodeURIComponent(incident?.incidentID || '')}/live-events`,
    iAccountID: incident?.iAccountID,
    iBuildingID: incident?.iBuildingID,
    app: 'IncidentAlert',
    enabled: Boolean(incident?.incidentID),
    fallbackMs: 15000,
    refreshKey: String(incident?.incidentID || ''),
    onRefresh: loadCoords,
  });

  // ✅ ADD: refresh button handler with hard 5s guard
  const handleRefreshMap = useCallback(() => {
    const now = Date.now();

    // hard guard (even if someone triggers the handler manually)
    if (cooldownUntilRef.current && now < cooldownUntilRef.current) return;

    cooldownUntilRef.current = now + 5000;
    setCooldownUntil(cooldownUntilRef.current);

    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => {
      cooldownUntilRef.current = 0;
      setCooldownUntil(0);
    }, 5000);

    loadCoords();
  }, [loadCoords]);

  const refreshDisabled =
    isRefreshing || (cooldownUntil && Date.now() < cooldownUntil);

  useEffect(() => {
    // ✅ NEW: clear markers when coords is empty (prevents stale dots)
    if (coords.length === 0) {
      markersRef.current.forEach(m => { m.map = null; });
      markersRef.current = [];
      return;
    }

    let cancelled = false;

    googleMapsLoader
      .load()
      .then(async google => {
        if (cancelled) return;
        const { Map } = await google.maps.importLibrary('maps');
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

        const mapOptions = {
          center: coords[0],
          zoom: 20,
          mapTypeId: 'satellite',
          mapId: '61b079060566d6ae8f97b2c7',
          tilt: 0,
          heading: 0,
          disableDefaultUI: false,
          zoomControl: true,
          zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
          fullscreenControl: true,
          mapTypeControl: true,
        };

        if (!mapRef.current) {
          mapRef.current = new Map(mapDivRef.current, mapOptions);
        } else {
          mapRef.current.setOptions(mapOptions);
          mapRef.current.setCenter(coords[0]);
        }

        markersRef.current.forEach(m => { m.map = null; });
        markersRef.current = [];

        coords.forEach(pt => {
          const dot = document.createElement('div');
          dot.style.width = '12px';
          dot.style.height = '12px';
          dot.style.borderRadius = '50%';
          dot.style.backgroundColor = VARIANT_COLORS[pt.variant] || '#ff0000';
          dot.style.boxShadow = '0 0 0 1.5px #ffffff';
          dot.style.pointerEvents = 'none';

          const adv = new AdvancedMarkerElement({
            map: mapRef.current,
            position: pt,
            content: dot,
          });
          markersRef.current.push(adv);

          const tel = cleanTel(pt.mobile);
          const info = new google.maps.InfoWindow({
            content: `
              <div style="
                min-width:220px;
                max-width:280px;
                padding:10px 12px;
                border-radius:10px;
                font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
                font-size:13px;
                line-height:1.25;
              ">
                <div style="font-weight:900; font-size:14px; margin-bottom:6px;">
                  ${escapeHtml(pt.sender || 'Unknown')}
                </div>

                ${tel ? `
                  <div style="margin:6px 0 8px 0;">
                    <a
                      href="tel:${tel}"
                      style="text-decoration:none; color:#0d6efd; font-weight:800;"
                      title="Tap to call"
                    >📞 ${escapeHtml(pt.mobile)}</a>
                  </div>
                ` : ''}

                <div style="display:flex; align-items:center; gap:8px; margin:6px 0;">
                  <div style="font-weight:800;">Status</div>
                  ${statusPillHtml(pt.statusLabel || '—', pt.variant)}
                </div>

                <div style="margin-top:6px; color:#666; font-size:12px;">
                  ${escapeHtml(new Date(pt.ts).toLocaleString())}
                </div>
              </div>
            `,
          });

          adv.addListener('gmp-click', () =>
            info.open({ map: mapRef.current, anchor: adv })
          );
        });
      })
      .catch(console.error);

    return () => { cancelled = true; };
  }, [coords]);

  useEffect(() => () => {
    // ✅ ADD: cleanup cooldown timer too
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);

    markersRef.current.forEach(m => { m.map = null; });
    markersRef.current = [];
    mapRef.current = null;
  }, []);

  // ✅ CHANGE: wrap map so we can show the button above it (no overlay conflicts)
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="d-flex justify-content-end mb-2">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={handleRefreshMap}
          disabled={refreshDisabled}
        >
          {isRefreshing ? 'Refreshing…' : 'Refresh Map'}
        </Button>
      </div>

      <div
        ref={mapDivRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}




// ───────────────────────────── Main Dashboard ───────────────────────────
 export default function Dashboard() {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { state } = useLocation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const initialSelectedIncident = state?.selectedIncident || state?.incident || null;

  /* ─── Local state ──────────────────────────────────────────────── */
  const [selectedIncident, setSelectedIncident] = useState(initialSelectedIncident);
  const [incidents24, setIncidents24] = useState([]);
  const iCampusID = state?.iCampusID ?? '-99';
  const iBuildingID = state?.iBuildingID ?? '-99';
  const [activeOnly, setActiveOnly] = useState(state?.activeOnly === '1');
  const [showIncidentsModal, setShowIncidentsModal] = useState(false);
  const [showSpecialStudents, setShowSpecialStudents] = useState(false);
  const [specialStudentsTab, setSpecialStudentsTab] = useState('search');
  const openSpecialStudents = (tab) => {
    setSpecialStudentsTab(tab);
    setShowSpecialStudents(true);
  };
const [showLocEditor, setShowLocEditor] = useState(false);

  // NEW: Help modal state
  const [showHelp, setShowHelp] = useState(false);
  const [showMessageSearch, setShowMessageSearch] = useState(false);

const isAdmin = useMemo(() => {
  const role = String(user?.userRole || user?.role || user?.Role || '')
    .replace(/\s+/g, '')
    .toLowerCase();

  return Boolean(
    user?.isSystemAdmin ||
    user?.isAdmin ||
    ['administrator', 'systemadministrator', 'usermanager'].includes(role)
  );
}, [user]);

const locEditorUserContext = useMemo(() => {
  const normalizedUser = {
    ...(user || {}),
    userID: user?.userID || user?.iUserID || user?.id || '',
  };

  return {
    iAccountID: selectedIncident?.iAccountID || user?.iAccountID || '',
    iCampusID: selectedIncident?.iCampusID || iCampusID,
    iBuildingID: selectedIncident?.iBuildingID || iBuildingID,
    user: normalizedUser,
  };
}, [selectedIncident, user, iCampusID, iBuildingID]);

const handleIncidentChange = useCallback((updatedIncident) => {
  const updated = updatedIncident?.incident || updatedIncident;
  if (!updated?.incidentID) return;

  setSelectedIncident(prev => (
    prev?.incidentID === updated.incidentID
      ? { ...prev, ...updated }
      : updated
  ));

  setIncidents24(prev => (
    prev.map(inc => (
      inc.incidentID === updated.incidentID
        ? { ...inc, ...updated }
        : inc
    ))
  ));
}, []);

// The selected incident header/active badge follows the same authorized stream.
const refreshSelectedIncident = useCallback(async (signal) => {
  const id = selectedIncident?.incidentID;
  if (!id) return;
  try {
    const { data } = await axios.get(`/ialert/incidents/${id}`, { signal });
    if (signal?.aborted || !data?.incidentID || String(data.incidentID) !== String(id)) return;
    setSelectedIncident(prev => {
      if (String(prev?.incidentID || '') !== String(id)) return prev;
      const next = { ...prev, ...data };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
    setIncidents24(prev => prev.map(inc => String(inc.incidentID) === String(id) ? { ...inc, ...data } : inc));
  } catch (error) {
    if (!signal?.aborted && error?.code !== 'ERR_CANCELED') console.error('load selected incident', error);
  }
}, [selectedIncident?.incidentID]);

useOperationalUpdates({
  client: axios,
  path: `/ialert/incidentmessages/${encodeURIComponent(selectedIncident?.incidentID || '')}/live-events`,
  iAccountID: selectedIncident?.iAccountID,
  iBuildingID: selectedIncident?.iBuildingID,
  app: 'IncidentAlert',
  enabled: Boolean(selectedIncident?.incidentID),
  fallbackMs: 15000,
  refreshKey: String(selectedIncident?.incidentID || ''),
  onRefresh: refreshSelectedIncident,
});

const handleToggleActive = useCallback(async () => {
  if (!selectedIncident?.incidentID) return;

  const currentlyActive =
    selectedIncident.isActive === true ||
    selectedIncident.isActive === 1 ||
    selectedIncident.isActive === '1';

  const nextIsActive = currentlyActive ? 0 : 1;

  try {
    const { data } = await axios.put(`/ialert/incidents/${selectedIncident.incidentID}`, {
      isActive: nextIsActive,
    });

    const updated = data?.incident || data || {
      ...selectedIncident,
      isActive: nextIsActive,
    };

    handleIncidentChange(updated);
  } catch (e) {
    console.error('Toggle incident active failed', e);
  }
}, [selectedIncident, handleIncidentChange]);


  useEffect(() => {
    const routedIncident = state?.selectedIncident || state?.incident || null;
    if (!routedIncident?.incidentID) return;

    setSelectedIncident(prev => {
      if (prev?.incidentID === routedIncident.incidentID) return prev;
      return routedIncident;
    });
  }, [state]);



  /* ─── Helper → fetch full history & pick newest ─────────────────── */
  const fetchLatestOverall = useCallback(() => {
    if (!user?.iAccountID) return;
    axios
      .get('/ialert/incidents', { params: { iAccountID: user.iAccountID, activeOnly: '0' } })
      .then(({ data }) => {
        if (data?.length) {
          const newest = [...data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )[0];
          setSelectedIncident(newest);
        }
      })
      .catch(console.error);
  }, [user?.iAccountID]);

  /* ─── Called whenever 24 h list loads ───────────────────────────── */
  const handleIncidentsLoaded = useCallback(
    list => {
      if (selectedIncident) return;
      if (list.length) {
        const sorted = [...list].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        const recentActive = sorted.find(i => i.isActive);
        setSelectedIncident(recentActive || sorted[0]);
      } else {
        fetchLatestOverall();
      }
    },
    [selectedIncident, fetchLatestOverall]
  );

  /* ─── Safety: ensure we try once at mount if nothing selected ───── */
  useEffect(() => {
    if (!selectedIncident && incidents24.length === 0) {
      fetchLatestOverall();
    }
  }, [selectedIncident, incidents24.length, fetchLatestOverall]);

  /* ─── Header badges: always include selectedIncident ────────────── */
  const headerBadges = useMemo(() => {
    if (
      selectedIncident &&
      !incidents24.some(i => i.incidentID === selectedIncident.incidentID)
    ) {
      return [selectedIncident, ...incidents24];
    }
    return incidents24;
  }, [incidents24, selectedIncident]);

const specialStudentScope = useMemo(() => {
  if (!selectedIncident) return null;

  return {
    incidentID: selectedIncident.incidentID,
    iAccountID: selectedIncident.iAccountID || user?.iAccountID,
    iCampusID: selectedIncident.iCampusID || iCampusID,
    iBuildingID: selectedIncident.iBuildingID || iBuildingID,
    districtSearch: !String(selectedIncident.iBuildingID || iBuildingID || '').trim()
      || String(selectedIncident.iBuildingID || iBuildingID) === '-99',
    buildingName: selectedIncident.buildingName || '',
    incidentType: selectedIncident.incidentType || '',
    incidentCategory: selectedIncident.incidentCategory || '',
  };
}, [selectedIncident, user?.iAccountID, iCampusID, iBuildingID]);



const selectedIncidentLabel = useMemo(() => {
  if (!selectedIncident) return '';

  const type = String(selectedIncident.incidentType || '').trim();
  const category = String(selectedIncident.incidentCategory || '').trim();
  const typeAndCategory = [type, category].filter(Boolean).join(' – ');
  const locationLabel = headerLocation(selectedIncident);

  return [typeAndCategory, locationLabel].filter(Boolean).join(' · ');
}, [selectedIncident]);


  /* ─── Panels definition ────────────────────────────────────────── */
  const panels = [
    {
      key: '0',
      title: 'Active Incidents (24 h)',
      content: (
        <ActiveIncidentsList
          selectedId={selectedIncident?.incidentID}
          onSelect={inc => setSelectedIncident(inc)}
          iCampusID={iCampusID}
          iBuildingID={iBuildingID}
          activeOnly={activeOnly}
          onLoaded={list => {
            setIncidents24(list);
            handleIncidentsLoaded(list);
          }}
          last24hOnly
        />
      ),
    },
    {
      key: '1',
      title: 'Latest Message Traffic',
      content: selectedIncident ? (
        <div style={{
          height: '60vh',
          display: 'flex',
          flexDirection: 'column-reverse',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}>
          <IncidentAlertChat
            incidentID={selectedIncident.incidentID}
            iAccountID={selectedIncident.iAccountID}
            iCampusID={selectedIncident.iCampusID}
            iBuildingID={selectedIncident.iBuildingID}
            buildingName={selectedIncident.buildingName}
            myLocation={selectedIncident.myLocation}
            myStatus={parseStatus(selectedIncident.myStatus)}
          />
        </div>
      ) : null,
    },
 {
  key: '2',
  title: 'People, Status & Location',
  content: selectedIncident ? (
    <>
    <PeopleStatus
      incidentID={selectedIncident.incidentID}
      iAccountID={selectedIncident.iAccountID}
      iBuildingID={selectedIncident.iBuildingID}
      buildingName={selectedIncident.buildingName}
      myLocation={selectedIncident.myLocation}
      statuses={parseStatus(selectedIncident.myStatus)}
    />
    </>
  ) : null,
},
    {
      key: '3',
      title: 'Map / Floor‑plan',
      content: isMobile ? (
        <div style={{ height: '60vh' }}>
          <MapView key={selectedIncident?.incidentID} incident={selectedIncident} />
        </div>
      ) : (
        <MapView key={selectedIncident?.incidentID} incident={selectedIncident} />
      ),
    },
  ];

  // ── DRILL overlay (selected incident scope)
  const isDrillSelected =
    String(selectedIncident?.incidentMode || '').toUpperCase() === 'DRILL';
  const drillOverlay = isDrillSelected ? (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1040,          // below Bootstrap modal (1050), above page content
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.1,
        fontSize: '12vw',
        fontWeight: 900,
        color: 'green',
        textTransform: 'uppercase',
        userSelect: 'none',
        transform: 'rotate(-30deg)',
      }}
    >
      This&nbsp;is&nbsp;a&nbsp;Drill
    </div>
  ) : null;




  /* ─── Mobile layout ─────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <>
      {drillOverlay}
        <DashboardCardHeader
          title={
            <div className="d-flex align-items-center gap-2">
              <span>Incident Monitor</span>
              <Button
                variant="warning"
                size="sm"
                className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 28, height: 28, lineHeight: 1,
                  backgroundColor: 'darkblue', color: '#fff',
                  border: '2px solid #fff',
                  boxShadow: '0 0 6px rgba(255,87,34,0.6)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#ff5722';
                  e.currentTarget.style.borderColor = '#d32f2f';
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'darkblue';
                  e.currentTarget.style.borderColor = 'white';
                  e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
                }}
                onClick={() => setShowHelp(true)}
                aria-label="How to use the Incident Monitor"
                title="How to use the Incident Monitor"
              >
                ?
              </Button>
            </div>
          }
          closePath="/reportincidentpanel"
          hideDropdown
          logoOverride={logo}
        />

        {/* Mobile header row */}
        <Container fluid className="pt-2">
          <Row className="gx-2 gy-2 align-items-center">
            <Col xs={12}>
              <h5 className="mb-1">Active Incidents (last 24 h)</h5>
            </Col>

    
    <Col xs={12}>
  <div className="d-flex flex-column gap-2">
    {headerBadges.map((inc) => {
      const type = inc.incidentType ?? '';
      const category = inc.incidentCategory || '';
      const label = category ? `${type} – ${category}` : type;
      const location = headerLocation(inc);

      return (
        <div
          key={inc.incidentID}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedIncident(inc)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedIncident(inc);
            }
          }}
          style={{
            background: '#d9534f',                // bootstrap danger-ish
            borderRadius: 10,
            padding: '10px 12px',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
          }}
          title={location ? `${label} · ${location}` : label}
        >
          {/* Line 1: type + category */}
          <div
            style={{
              fontWeight: 800,
              fontSize: '1rem',
              lineHeight: 1.15,
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {label}
          </div>

          {/* Line 2: location pill (readable) */}
          {location && (
            <div
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                padding: '4px 10px',
                borderRadius: 9999,
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(0,0,0,0.18)',
                color: '#212529',
                fontSize: '0.9rem',
                fontWeight: 700,
                lineHeight: 1.15,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
              }}
            >
              {location}
            </div>
          )}
        </div>
      );
    })}
  </div>
</Col>











            <Col xs={12}>


 <ButtonGroup className="w-100 flex-wrap">
  <Button
    size="sm"
    variant="primary"
    onClick={() => setShowIncidentsModal(true)}
  >
    Select Incident
  </Button>

  <Button
    size="sm"
    variant="warning"
    onClick={() => openSpecialStudents('search')}
    disabled={!selectedIncident}
  >
    Special
  </Button>

  <Button
    size="sm"
    variant="outline-primary"
    onClick={() => openSpecialStudents('accommodations')}
    disabled={!selectedIncident}
  >
    Student Accommodations
  </Button>

  {isAdmin && (
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={() => setShowLocEditor(true)}
      disabled={!selectedIncident}
      title="Administration"
      aria-label="Admin"
    >
      <GearFill size={16} className="me-1" />
      Manage
    </Button>
  )}
</ButtonGroup>


            </Col>
          </Row>

          {/* Accordion panels */}
          <Accordion defaultActiveKey="1" className="mt-3">
            {panels
              .filter(p => p.key !== '0')
              .map(p => (
                <Accordion.Item eventKey={p.key} key={p.key}>
                  <div className="d-flex align-items-center">
                    <Accordion.Header className="flex-grow-1 mb-0">
                      {p.title}
                    </Accordion.Header>
                    {p.key === '1' && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2 flex-shrink-0 d-inline-flex align-items-center gap-1"
                        onClick={() => setShowMessageSearch(true)}
                        disabled={!selectedIncident}
                        aria-label="Search messages for the selected incident"
                      >
                        <Search size={13} />
                        Search Messages
                      </Button>
                    )}
                  </div>
                  <Accordion.Body>
                    {p.content || <p className="text-muted">Select an incident to view content.</p>}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
          </Accordion>
        </Container>

        {/* Help modal (mobile) */}
        <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title>How to use the Incident Monitor</Modal.Title>
          </Modal.Header>
          <Modal.Body>
          <ol className="mb-0 ps-3">

             <li>The Incident Monitor opens to the most recent active incident. Use <b>Select Incident</b> to open the incident list, then use <b>Show Active/Show All</b> inside that modal to filter.</li>
          
            <li><b>Latest Message Traffic:</b> live thread for the selected incident. Use <b>Search Messages</b> to find matching messages and see every message from each matching person.</li>

            <li>
             <b>People, Status & Location:</b> medical attention is first, missing people are second, and other checked-in people follow. Select either <b>check-in status bar</b> to open the full-screen grouped roster with separate check-in progress bars. Click the message count to view a person’s message history.
              <div className="mt-1 ms-3 small">
                <Badge bg="danger" className="me-2">need medical attention</Badge>
                <Badge bg="warning" text="dark" className="me-2">someone is missing</Badge>
                <Badge bg="success">everyone is here</Badge>
              </div>
            </li>
            <li><b>Map / Floor‑plan:</b> markers colored by status; click a marker for details.</li>
          </ol>
            <div className="mt-3 small text-muted">Lists and progress bars auto-refresh as people check in.</div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowHelp(false)}>Got it</Button>
          </Modal.Footer>
        </Modal>

        {/* Modal for selecting incidents (mobile shares modal) */}
         {/* Modal for selecting incidents (mobile shares modal) */}
        <Modal
          show={showIncidentsModal}
          onHide={() => setShowIncidentsModal(false)}
          size="lg"
          backdrop="static"
          scrollable
        >
          <Modal.Header closeButton>
            <div className="d-flex justify-content-between align-items-center w-100 me-3">
              <Modal.Title>All Incidents</Modal.Title>

              <Button
                size="sm"
                variant={activeOnly ? 'secondary' : 'primary'}
                onClick={() => setActiveOnly(prev => !prev)}
              >
                {activeOnly ? 'Show All' : 'Show Active'}
              </Button>
            </div>
          </Modal.Header>

          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: 0 }}>
            <ActiveIncidentsList
              selectedId={selectedIncident?.incidentID}
              onSelect={inc => {
                setSelectedIncident(inc);
                setShowIncidentsModal(false);
              }}
              iCampusID={iCampusID}
              iBuildingID={iBuildingID}
              activeOnly={activeOnly}
              onLoaded={() => {}}
              last24hOnly={false}
            />
          </Modal.Body>
        </Modal>

        {selectedIncident && (
          <LocationEditorModal
            show={showLocEditor}
            onHide={() => setShowLocEditor(false)}
            userContext={locEditorUserContext}
            toggleActive={handleToggleActive}
            incident={selectedIncident}
            onIncidentChange={handleIncidentChange}
          />
        )}

        <IncidentMessageSearchModal
          show={showMessageSearch}
          onHide={() => setShowMessageSearch(false)}
          incidentID={selectedIncident?.incidentID}
          incidentLabel={selectedIncidentLabel}
        />
      </>
    );
  }
 

  /* ─── Desktop layout ───────────────────────────────────────────── */
  return (
    <>
    {drillOverlay}
      <Card style={{ width: '100vw', height: '100vh', border: 'none', borderRadius: 0 }}>
        <DashboardCardHeader
          title={
            <div className="d-flex align-items-center gap-2">
              <span>Incident Monitor</span>
              <Button
                variant="warning"
                size="sm"
                className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 28, height: 28, lineHeight: 1,
                  backgroundColor: 'darkblue', color: '#fff',
                  border: '2px solid white',
                  boxShadow: '0 0 6px rgba(255,87,34,0.6)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#ff5722';
                  e.currentTarget.style.borderColor = '#d32f2f';
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(211,47,47,0.8)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'darkblue';
                  e.currentTarget.style.borderColor = 'white';
                  e.currentTarget.style.boxShadow = '0 0 6px rgba(255,87,34,0.6)';
                }}
                onClick={() => setShowHelp(true)}
                aria-label="How to use the Incident Monitor"
                title="How to use the Incident Monitor"
              >
                ?
              </Button>
            </div>
          }
          closePath="/reportincidentpanel"
          hideDropdown
          logoOverride={logo}
        />

        
        <Card.Body className="pt-2 pb-3 overflow-auto">
          <Container fluid className="h-100 d-flex flex-column">
            {/* Header row */}
 <Row className="mb-3 gx-3 align-items-start">
  {/* 2x width vs buttons on large displays */}
  <Col lg={8} md={8}>
    <h5 className="mb-2">Active Incidents (last&nbsp;24&nbsp;h)</h5>

    <div className="d-flex flex-wrap gap-2">
      {headerBadges.map((inc) => {
        const label = `${inc.incidentType} – ${inc.incidentCategory || ''}`.trim();
        const location = headerLocation(inc);

        return (
          <Badge
            key={inc.incidentID}
            bg={srpColour(inc.incidentType)}
            className="mb-1"
            title={location ? `${label} · ${location}` : label}
            onClick={() => setSelectedIncident(inc)}
            style={{
              cursor: 'pointer',
              padding: '0.55rem 0.85rem',
              borderRadius: 10,

              // fixes the "narrow red rectangle" problem
              maxWidth: 'min(100%, 52rem)',

              // overrides Bootstrap badge defaults that make it look cramped/centered
              whiteSpace: 'normal',
              textAlign: 'left',
              lineHeight: 1.2,

              // subtle polish (optional but helps a lot visually)
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            }}
          >
            <span
              style={{
                display: 'grid',
                gridTemplateColumns: location ? '1fr auto' : '1fr',
                columnGap: '0.75rem',
                rowGap: '0.25rem',
                alignItems: 'start',
                minWidth: 0,
              }}
            >
              <span
                style={{
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  fontSize: '1.05rem',
                  fontWeight: 800,
                }}
              >
                {label}
              </span>

              {location && (
                <span
                  style={{
                    justifySelf: 'end',
                    alignSelf: 'start',

                    padding: '0.2rem 0.65rem',
                    borderRadius: 9999,
                    background: 'rgba(255,255,255,0.92)',
                    border: '1px solid rgba(0,0,0,0.18)',
                    color: '#212529',

                    fontSize: '0.9rem',
                    fontWeight: 700,
                    lineHeight: 1.1,

                    // prevents the chip from making the whole badge look like a giant block
                    maxWidth: '18rem',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {location}
                </span>
              )}
            </span>
          </Badge>
        );
      })}
    </div>
  </Col>

  {/* Buttons column */}
 {/* Buttons column */}
<Col lg={4} md={4} className="d-flex justify-content-end align-items-start flex-wrap gap-2">
  <Button
    size="sm"
    variant="outline-primary"
    onClick={() => navigate('/reportincidentpanel')}
  >
    Go&nbsp;Back
  </Button>

  <Button
    size="sm"
    variant="primary"
    onClick={() => setShowIncidentsModal(true)}
  >
    Select&nbsp;Incident
  </Button>

  <Button
    size="sm"
    variant="warning"
    onClick={() => openSpecialStudents('search')}
    disabled={!selectedIncident}
  >
    Special
  </Button>

  <Button
    size="sm"
    variant="outline-primary"
    onClick={() => openSpecialStudents('accommodations')}
    disabled={!selectedIncident}
  >
    Student Accommodations
  </Button>

  {isAdmin && (
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={() => setShowLocEditor(true)}
      disabled={!selectedIncident}
      title="Administration"
      aria-label="Admin"
      className="d-inline-flex align-items-center gap-1"
    >
      <GearFill size={16} />
      Manage Incident
    </Button>
  )}
</Col>


</Row>

 
            {/* Content row */}
            <Row className="gx-3 flex-grow-1" style={{ minHeight: 0 }}>
              {/* Thread */}
              <Col md={4} style={{ height: '100%' }}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: '1rem',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <h5 className="text-center mb-0 flex-grow-1">
                      Latest Message Traffic
                    </h5>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="flex-shrink-0 d-inline-flex align-items-center gap-1"
                      onClick={() => setShowMessageSearch(true)}
                      disabled={!selectedIncident}
                      aria-label="Search messages for the selected incident"
                    >
                      <Search size={13} />
                      Search Messages
                    </Button>
                  </div>
                  <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
                    {panels[1].content || (
                      <p className="text-muted">Select an incident to view its thread.</p>
                    )}
                  </div>
                </div>
              </Col>

              {/* People & Status */}
              <Col md={4} style={{ height: '100%' }}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: '1rem',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <h5 className="text-center mb-1">People, Status &amp; Location</h5>
                  <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
                    {panels[2].content || (
                      <p className="text-muted">Select an incident to view people & status.</p>
                    )}
                  </div>
                </div>
              </Col>

              {/* Map */}
              <Col md={4} style={{ height: '100%' }}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: '1rem',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <h5 className="text-center mb-3">Map / Floor‑plan</h5>
                  <div style={{ flex: '1 1 auto' }}>{panels[3].content}</div>
                </div>
              </Col>
            </Row>
          </Container>
        </Card.Body>


      </Card>

      {/* Help modal (desktop) */}
      <Modal show={showHelp} onHide={() => setShowHelp(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>How to use the Incident Monitor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ol className="mb-0 ps-3">
             
           <li>The Incident Monitor opens to the most recent active incident. Use <b>Select Incident</b> to open the incident list, then use <b>Show Active/Show All</b> inside that modal to filter.</li>
           
           
            <li><b>Latest Message Traffic:</b> live thread for the selected incident. Use <b>Search Messages</b> to find matching messages and see every message from each matching person.</li>

            <li>
             <b>People, Status & Location:</b> medical attention is first, missing people are second, and other checked-in people follow. Select either <b>check-in status bar</b> to open the full-screen grouped roster with separate check-in progress bars. Click the message count to view a person’s message history.
              <div className="mt-1 ms-3 small">
                <Badge bg="danger" className="me-2">need medical attention</Badge>
                <Badge bg="warning" text="dark" className="me-2">someone is missing</Badge>
                <Badge bg="success">everyone is here</Badge>
              </div>
            </li>
            <li><b>Map / Floor‑plan:</b> markers colored by status; click a marker for details.</li>
          </ol>
          <div className="mt-3 small text-muted">Lists and progress bars auto-refresh as people check in.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowHelp(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Incidents modal (desktop shares same instance) */}
       {/* Incidents modal (desktop shares same instance) */}
      <Modal
        show={showIncidentsModal}
        onHide={() => setShowIncidentsModal(false)}
        size="lg"
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100 me-3">
            <Modal.Title>All Incidents</Modal.Title>

            <Button
              size="sm"
              variant={activeOnly ? 'secondary' : 'primary'}
              onClick={() => setActiveOnly(prev => !prev)}
            >
              {activeOnly ? 'Show All' : 'Show Active'}
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: 0 }}>
          <ActiveIncidentsList
            selectedId={selectedIncident?.incidentID}
            onSelect={inc => {
              setSelectedIncident(inc);
              setShowIncidentsModal(false);
            }}
            iCampusID={iCampusID}
            iBuildingID={iBuildingID}
            activeOnly={activeOnly}
            onLoaded={() => {}}
            last24hOnly={false}
          />
        </Modal.Body>
      </Modal>

      {selectedIncident && (
        <LocationEditorModal
          show={showLocEditor}
          onHide={() => setShowLocEditor(false)}
          userContext={locEditorUserContext}
          toggleActive={handleToggleActive}
          incident={selectedIncident}
          onIncidentChange={handleIncidentChange}
        />
      )}

      <IncidentMessageSearchModal
        show={showMessageSearch}
        onHide={() => setShowMessageSearch(false)}
        incidentID={selectedIncident?.incidentID}
        incidentLabel={selectedIncidentLabel}
      />

<IncidentSpecialStudentsModal


  show={showSpecialStudents}
  onHide={() => setShowSpecialStudents(false)}
  scope={specialStudentScope}
  initialTab={specialStudentsTab}
  user={user}
  actorName={
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.username ||
    ''
  }
/>









    </>
  );
}

 

 

 

 

 

 

 

 
 

 
 

 

 

 

 

 
 

 